<template>
  <Loader v-if="isLoading"></Loader>
  <Alert
    v-if="!isLoading && errorMsgs.length"
    key="submit-errors"
    dismiss-by-native=""
    type="danger"
    :messages="errorMsgs"
    @close="removeErrors"></Alert>

  <div class="b-block b-block--transparent b-block--sm-pad s-confirmation-form">
    <div class="s-confirmation-form__head mb-0">
      <h3 class="s-confirmation-form__head__title">
        {{ dictionary.confirmationTitle }}
      </h3>
      <h5
        class="s-confirmation-form__head__desc"
        v-html="dictionary.confirmationDesc"></h5>
    </div>

    <div class="s-confirmation-form__main">
      <EmailForm
        :completed-step="completedStep"
        :email="email"
        @change-data="toggleChangeModal"
        @prev-step-completed="enablePhoneOtp"
        @send-email-otp="getEmailOtp"
        @confirm-email-success="requestPhoneOtp"></EmailForm>

      <PhoneForm
        :mobile-telephone="mobileTelephone"
        :dialing-code="selectedCountryCode"
        :prev-step-completed="prevStepCompleted"
        @change-data="togglePhoneModal"
        @send-phone-otp="getPhoneOtp"></PhoneForm>
    </div>
  </div>

  <div class="s-confirmation-form__submit-btn">
    <button
      class="btn-primary-orange"
      type="button"
      :disabled="disabledBtn"
      @click="sendAllData">
      <span>{{ dictionary.submitBtnLabel }}</span>
    </button>
  </div>

  <!--change email data-->
  <ModalOverlay
    v-model="showChangeModal"
    no-close-backdrop
    background-class="b-pop-up--alert-success b-pop-up__holder b-pop-up--change-email"
    header-background-class="b-pop-up__header"
    @close="() => toggleChangeModal(false)">
    <Loader v-if="isLoading"></Loader>
    <template #header>
      <div class="b-pop-up__header__title">
        {{ dictionary.emailModalLabel }}
      </div>
    </template>
    <template #body>
      <div class="b-pop-up__alert-success__content" style="padding-top: unset">
        <div class="b-pop-up__alert-success__content__container">
          <div class="b-pop-up__email">
            <form autocomplete="off" @submit.prevent="submitNewEmail">
              <div class="form-floating mb-32">
                <input
                  id="newEmail"
                  v-model.trim="newEmail"
                  name="newEmail"
                  type="email"
                  class="form-control"
                  :placeholder="dictionary.emailPlaceholder"
                  :class="{ 'form-control--error': v$.newEmail.$error }"
                  @blur="v$.newEmail.$touch" />

                <label for="newEmail" class="form-label">
                  {{ dictionary.emailLabel }}
                </label>

                <!-- validations -->
                <template v-if="v$.newEmail.$error">
                  <div
                    v-if="v$.newEmail.required.$invalid"
                    class="field-validation-error">
                    {{ dictionary.emailRequired }}
                  </div>
                  <div
                    v-if="v$.newEmail.email.$invalid"
                    class="field-validation-error">
                    {{ dictionary.emailRegex }}
                  </div>
                </template>

                <div
                  v-if="!isLoading && errorMsgs.length"
                  class="field-validation-error">
                  {{ errorMsgs[0] }}
                </div>
              </div>

              <button
                type="submit"
                class="p-contact__submit-btn"
                :disabled="v$.newEmail.$invalid">
                <span>{{ dictionary.change }}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </template>
  </ModalOverlay>

  <!--change phone data-->
  <ModalOverlay
    v-model="showPhoneModal"
    no-close-backdrop
    background-class="b-pop-up--alert-success b-pop-up__holder b-pop-up--change-phone"
    header-background-class="b-pop-up__header"
    @close="() => togglePhoneModal(false)">
    <Loader v-if="isLoading"></Loader>
    <template #header>
      <div class="b-pop-up__header__title">
        {{ dictionary.phoneModalLabel }}
      </div>
    </template>
    <template #body>
      <div class="b-pop-up__alert-success__content" style="padding-top: unset">
        <div class="b-pop-up__alert-success__content__container">
          <div class="b-pop-up__email">
            <form autocomplete="off" @submit.prevent="submitNewPhone">
              <div class="b-block__flex">
                <div class="p-interest-form">
                  <div class="p-interest-form__form-container">
                    <div class="form-group">
                      <div class="d-flex" style="column-gap: 0.5em">
                        <div class="form-col" style="width: 30%">
                          <div class="form-floating">
                            <select
                              id="newCountryCode"
                              v-model="newSelectedCountyCode"
                              name="newCountryCode"
                              class="form-select"
                              :class="{
                                'form-select--error':
                                  v$.newSelectedCountyCode.$error,
                              }"
                              @blur="v$.newSelectedCountyCode.$touch">
                              <option value="" disabled>
                                {{ dictionary.dialingCodePlaceholder }}
                              </option>
                              <option
                                v-for="item in countryOptions"
                                :key="item"
                                :value="item">
                                {{ item }}
                              </option>
                            </select>

                            <label for="newCountryCode" class="form-label">
                              {{ dictionary.dialingCodeLabel }}
                            </label>

                            <!-- validations -->
                            <template v-if="v$.newSelectedCountyCode.$error">
                              <div
                                v-if="
                                  v$.newSelectedCountyCode.required.$invalid
                                "
                                class="field-validation-error">
                                {{ dictionary.dialingCodeRequired }}
                              </div>
                            </template>
                          </div>
                        </div>
                        <div class="form-col" style="flex: 1">
                          <div class="form-floating">
                            <input
                              id="newPhone"
                              v-model.trim="newPhone"
                              name="newPhone"
                              type="text"
                              class="form-control"
                              :placeholder="
                                dictionary.mobileTelephonePlaceholder
                              "
                              :class="{
                                'form-control--error': v$.newPhone.$error,
                              }"
                              @blur="v$.newPhone.$touch" />

                            <label for="newPhone" class="form-label">
                              {{ dictionary.mobileTelephoneLabel }}
                            </label>

                            <!-- validations -->
                            <template v-if="v$.newPhone.$error">
                              <div
                                v-if="v$.newPhone.required.$invalid"
                                class="field-validation-error">
                                {{ dictionary.mobileTelephoneRequired }}
                              </div>
                              <div
                                v-if="v$.newPhone.regex.$invalid"
                                class="field-validation-error">
                                {{ dictionary.mobileTelephoneRegex }}
                              </div>
                            </template>

                            <div
                              v-if="!isLoading && errorMsgs.length"
                              class="field-validation-error">
                              {{ errorMsgs[0] }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                class="p-contact__submit-btn"
                :disabled="v$.newPhone.$invalid">
                <span>{{ dictionary.change }}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </template>
  </ModalOverlay>
</template>

<script>
import * as lib from '../../../lib/lib'
import constants from '../../../lib/constants'
import EmailForm from './EmailForm.vue'
import PhoneForm from './PhoneForm.vue'
import ModalOverlay from '../ModalOverlay.vue'
import Loader from '../Shared/Loader.vue'
import useVuelidate from '@vuelidate/core'
import postRequestHandler from '../../../lib/postRequestHandler'
import getErrorMessage from '../../../lib/getErrorMessage'
import Alert from '../Shared/Alert.vue'
import { useBookingStore } from '../../store/BookingStore'

const { telephone } = constants.regex
const DEFAULT_COUNTRY_CODE = '30'
import { email, helpers, required } from '@vuelidate/validators'

export default {
  /* eslint-disable vue/no-reserved-component-names */
  components: {
    EmailForm,
    PhoneForm,
    ModalOverlay,
    Loader,
    Alert,
  },
  props: {
    firstName: {
      default: '',
      type: String,
    },
    lastName: {
      default: '',
      type: String,
    },
    email: {
      default: '',
      type: String,
    },
    mobileTelephone: {
      default: '',
      type: String,
    },
    dateOfBirth: {
      default: '',
      type: String,
    },
    zip: {
      default: '',
      type: String,
    },
    contactByThirdParty: {
      default: false,
      type: Boolean,
    },
    acceptTerms: {
      default: false,
      type: Boolean,
    },
    gender: {
      default: '',
      type: String,
    },
    selectedCountryCode: {
      default: '',
      type: String,
    },
    countryOptions: {
      default: null,
      type: Array,
    },
  },
  emits: ['changed-email', 'changed-phone', 'changed-country-code'],
  setup() {
    return { v$: useVuelidate(), store: useBookingStore() }
  },

  data() {
    return {
      isLoading: false,
      errorMsgs: [],
      completedStep: false,
      prevStepCompleted: false,
      showChangeModal: false,
      showPhoneModal: false,
      newEmail: '',
      newPhone: '',
      newSelectedCountyCode: '',
      emailOtp: '',
      phoneOtp: '',
      //  successChangeEmail: false,
    }
  },
  validations() {
    return {
      newPhone: {
        required,
        regex: helpers.regex(telephone),
      },
      newEmail: {
        email,
        required,
      },
      newSelectedCountyCode: {
        required,
      },
    }
  },

  computed: {
    disabledBtn() {
      return this.emailOtp !== '' && this.phoneOtp !== '' ? false : true
    },
  },

  mounted() {
    // Set default country code
    if (this.selectedCountryCode) {
      this.newSelectedCountyCode = this.selectedCountryCode
    } else if (this.countryOptions && this.countryOptions.length > 0) {
      const defaultOption = this.countryOptions.find(
        (option) => option === DEFAULT_COUNTRY_CODE
      )
      if (defaultOption) {
        this.newSelectedCountyCode = DEFAULT_COUNTRY_CODE
      }
    }
  },

  methods: {
    removeErrors() {
      this.errorMsgs = []
    },
    toggleChangeModal(show) {
      this.showChangeModal = show
    },

    togglePhoneModal(show) {
      this.showPhoneModal = show
    },

    getEmailOtp(otp) {
      this.emailOtp = otp
    },

    getPhoneOtp(otp) {
      this.phoneOtp = otp
    },

    async submitNewEmail() {
      if (this.isLoading) {
        return false
      }

      this.isLoading = true
      this.modifiedEmail = this.newEmail

      const postData = {
        userSessionId: this.store.bookingProgress.usersessionId,
        email: this.newEmail,
        language: document.documentElement.lang,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointChangeEmail,
        successCallback: this.handleSuccessNewMail,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.emailChangeCatpchaAction,
      })
    },

    handleSuccessNewMail() {
      this.isLoading = false
      this.errorMsgs = []
      this.$emit('changed-email', this.newEmail)
      this.store.otpFormData.email = this.newEmail

      this.toggleChangeModal(false)
      this.newEmail = ''
      this.v$.$reset()
    },

    handleError(messages) {
      this.isLoading = false
      this.hasError = true
      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )
    },

    async submitNewPhone() {
      if (this.isLoading) {
        return false
      }

      this.isLoading = true
      this.modifiedPhone = this.newPhone

      const postData = {
        userSessionId: this.store.bookingProgress.usersessionId,
        phone: this.newPhone,
        countryCode: this.newSelectedCountyCode,
        language: document.documentElement.lang,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointChangePhone,
        successCallback: this.handleSuccessNewPhone,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.phoneChangeCatpchaAction,
      })
    },

    handleSuccessNewPhone() {
      this.isLoading = false
      this.errorMsgs = []

      this.$emit('changed-phone', this.newPhone)
      this.$emit('changed-country-code', this.newSelectedCountyCode)
      this.store.otpFormData.phone = this.newPhone
      this.store.otpFormData.dialingCode = this.newSelectedCountyCode

      this.togglePhoneModal(false)
      this.newPhone = ''
      this.v$.$reset()
    },

    enablePhoneOtp(show) {
      this.prevStepCompleted = show
    },

    async requestPhoneOtp() {
      if (this.isLoading) {
        return false
      }

      this.isLoading = true

      const phonePayload = {
        mobilePhone: this.store.otpFormData.phone,
        areaCode: this.store.otpFormData.dialingCode,
        userSessionId: this.store.bookingProgress.usersessionId,
        language: document.documentElement.lang,
      }

      await postRequestHandler({
        postData: phonePayload,
        endpoint: this.configuration.endpointRequestPhoneOtp,
        successCallback: this.handlePhoneOtpRequestSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.phoneOtpCatpchaAction,
      })
    },

    handlePhoneOtpRequestSuccess() {
      this.isLoading = false
      this.errorMsgs = []
    },

    async sendAllData() {
      if (this.isLoading) {
        return false
      }

      this.isLoading = true

      const postData = {
        ...this.store.interestFormData,
        userSessionId: this.store.bookingProgress.usersessionId,
        language: document.documentElement.lang,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitAllData,
        successCallback: this.handleSuccess,
        errorCallback: this.handleSendAllDataError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.finishCatpchaAction,
      })
    },
    handleSuccess(response) {
      this.isLoading = false
      this.errorMsgs = []

      window.location.replace(response.data.redirectUrl)
    },

    async handleSendAllDataError(messages) {
      this.isLoading = false
      this.hasError = true
      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )

      await this.$nextTick()
      lib.scrollToError()
    },
  },
}
</script>
